import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import QRCode from 'qrcode';

import { Grid, Typography, IconButton, Chip, Tooltip, Backdrop, CircularProgress } from '@material-ui/core';
import { Close, AccountBalance, Receipt } from '@material-ui/icons';
import Loader from 'components/Loader';
import MTable from 'components/MTable';
import TableMobile from 'components/TableMobile';
import Button from 'components/Button';
import Select from 'components/Select';
import Input from 'components/Input';
import Modal from 'components/Modal';
import ListClientsFirstCharge from 'components/ListClientsFirstCharge';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBill, faPen, faRotateRight, faBarcode, faQrcode, faReceipt, faCopy, faEnvelope, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';

import { loadClientArea, updateReprocessCreditCardCharge, sendClientAreaChargeEmail } from '../store/thunk';
import { generateToken } from '../store/services';
import { removeSuperLogicaDebitWarning } from 'components/Filter/store/services';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import { convertToReal } from 'helpers/converters';
import { emailRegExp } from 'helpers/regex';

import { useStyles } from './styles';

const subscriptionHeaders = [
  { title: 'Negócio', field: 'business', sortable: false },
  { title: 'Detalhes do plano', field: 'planDetails', sortable: false },
  { title: 'Cobrança', field: 'charge', sortable: false }
];

const chargeHeaders = [
  { title: 'Situação', field: 'situation', sortable: false },
  { title: 'Vencimento', field: 'dueDate', sortable: false },
  { title: 'Negócio', field: 'business', sortable: false },
  { title: 'Forma de pagamento', field: 'paymentMethod', sortable: false },
  { title: 'Valor', field: 'amount', sortable: false }
];

const chargeStatusOptions = [
  { label: 'Em aberto', value: '0' },
  { label: 'Liquidada', value: '1' }
];

const chargeCompetenceOptions = [
  { label: 'Atual', value: 'current' },
  { label: 'Futura', value: 'future' }
];

const subscriptionsPreviewSize = 3;
const chargesPageSize = 10;

const maskCNPJ = v => {
  v = v?.replace(/\D/g, '');

  let len = v?.length;

  if (len < 12) {
    v = v
      ?.replace(/(\d{3})(\d)/, '$1.$2')
      ?.replace(/(\d{3})(\d)/, '$1.$2')
      ?.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    v = v
      ?.replace(/^(\d{2})(\d)/, '$1.$2')
      ?.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      ?.replace(/\.(\d{3})(\d)/, '.$1/$2')
      ?.replace(/(\d{4})(\d)/, '$1-$2');
  }

  return v?.substring(0, 18);
}

const formatCharge = charge => {
  const { amount, installments, installmentsAmount } = charge || {};
  return `${convertToReal(amount)} em ${installments}x de ${convertToReal(installmentsAmount)}`;
}

const formatDate = date => !!date ? moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY') : '--';

const getDaysLate = (liquidationDate, dueDate) => {
  if(!liquidationDate || !dueDate) {
    return 0;
  }

  return moment(liquidationDate, 'YYYY-MM-DD').diff(moment(dueDate, 'YYYY-MM-DD'), 'days');
}

const renderChargeNf = (nf, typographyProps) => {
  const { number, link } = nf || {};

  if(!number) {
    return null;
  }

  const nfNumber = <Typography {...typographyProps}>NFS {number}</Typography>;

  if(!link) {
    return nfNumber;
  }

  return(
    <a href={link} target='_blank' rel='noopener noreferrer'>
      NFS {number}
    </a>
  );
}

const buildFilterOptions = (list, labelField, valueField) => {
  const values = new Set(list?.map(item => item?.[valueField])?.filter(value => !!value));

  return [...values].map(value => ({
    label: list?.find(item => item?.[valueField] === value)?.[labelField] || '--',
    value
  }));
}

export default function ListClients() {
  const classes = useStyles();

  const dispatch = useDispatch();

  const history = useHistory();

  const { width } = useWindowDimensions();
  const isMobile = width < 960;

  const isFromLogin = history?.location?.state?.data?.isFromLogin;

  const { token } = useSelector(state => state.login);
  const { userId, resaleId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isAdminUser } = useSelector(state => state.profile.submit);
  const { isLoading, isSendEmailLoading, subscriptions, charges, futureCharges } = useSelector(state => state.listClients);
  const situationEstablishments = useSelector(state => state.login.situation.establishments);

  const isUserAdmin = +isAdminUser == 1;
  const isDebitWarning = situationEstablishments?.some(establishment => +establishment?.debitWarning == 1);

  const [isFirstChargeModalOpen, setIsFirstChargeModalOpen] = useState(false);
  const [firstChargeSubscription, setFirstChargeSubscription] = useState(null);

  const [isRemoveDebitWarningModalOpen, setIsRemoveDebitWarningModalOpen] = useState(false);
  const [isPixPaymentModalOpen, setIsPixPaymentModalOpen] = useState(false);
  const [pixPaymentModalData, setPixPaymentModalData] = useState({});

  const [isSendChargeEmailModalOpen, setIsSendChargeEmailModalOpen] = useState(false);
  const [sendChargeEmailData, setSendChargeEmailData] = useState({ id: null, email: '' });
  const [sendChargeEmailError, setSendChargeEmailError] = useState('');

  const [isShowingAllSubscriptions, setIsShowingAllSubscriptions] = useState(false);
  const [visibleChargesSize, setVisibleChargesSize] = useState(chargesPageSize);
  const [subscriptionBusinessFilter, setSubscriptionBusinessFilter] = useState('');
  const [subscriptionEstablishmentFilter, setSubscriptionEstablishmentFilter] = useState('');
  const [appliedSubscriptionFilters, setAppliedSubscriptionFilters] = useState({ superlogicaId: '', identifierExtra: '' });
  const [chargeStatusFilter, setChargeStatusFilter] = useState('');
  const [chargeCompetenceFilter, setChargeCompetenceFilter] = useState('current');
  const [appliedChargeFilters, setAppliedChargeFilters] = useState({ statusId: '', competence: 'current' });

  const roleFilteredSubscriptions = isUserAdmin
    ? subscriptions
    : subscriptions?.filter(item => +item?.plan?.id != 133 || +item?.plan?.id != 1);

  const subscriptionBusinessOptions = buildFilterOptions(roleFilteredSubscriptions, 'fantasyName', 'superlogicaId');
  const subscriptionEstablishmentOptions = buildFilterOptions(roleFilteredSubscriptions, 'identifier', 'identifierExtra');

  const chargesSource = appliedChargeFilters?.competence == 'future' ? futureCharges : charges;

  const isSubscriptionFilterActive = !!appliedSubscriptionFilters?.superlogicaId || !!appliedSubscriptionFilters?.identifierExtra;

  const filteredSubscriptions = roleFilteredSubscriptions?.filter(item => {
    const hasBusinessMatch = !appliedSubscriptionFilters?.superlogicaId || item?.superlogicaId == appliedSubscriptionFilters?.superlogicaId;
    const hasEstablishmentMatch = !appliedSubscriptionFilters?.identifierExtra || item?.identifierExtra == appliedSubscriptionFilters?.identifierExtra;

    return hasBusinessMatch && hasEstablishmentMatch;
  });

  const visibleSubscriptions = isShowingAllSubscriptions
    ? filteredSubscriptions
    : filteredSubscriptions?.slice(0, subscriptionsPreviewSize);

  const hasHiddenSubscriptions = !isShowingAllSubscriptions && filteredSubscriptions?.length > subscriptionsPreviewSize;

  const isChargeFilterActive = !!appliedChargeFilters?.statusId || appliedChargeFilters?.competence == 'future';

  const filteredCharges = chargesSource?.filter(item => {
    const hasEstablishmentMatch = !appliedSubscriptionFilters?.identifierExtra || item?.identifierExtra == appliedSubscriptionFilters?.identifierExtra;
    const hasStatusMatch = !appliedChargeFilters?.statusId || item?.statusId == appliedChargeFilters?.statusId;

    return hasEstablishmentMatch && hasStatusMatch;
  });

  const visibleCharges = filteredCharges?.slice(0, visibleChargesSize);
  const hiddenChargesSize = (filteredCharges?.length || 0) - (visibleCharges?.length || 0);

  const getSubscriptionEmail = subscription => {
    const email = subscription?.email;

    if(!email) {
      return '';
    }

    return email?.includes(';') ? email?.split(';')[0] : email;
  }

  const [isSuperlogicaUserLoading, setIsSuperlogicaUserLoading] = useState(isFromLogin);

  const [superLogicaUser, setSuperLogicaUser] = useState({
    superlogicaId: '',
    email: ''
  });

  const hasLoadedSubscriptions = useRef(false);
  const hasProcessedLoginRedirect = useRef(false);

  useEffect(() => {
    dispatch(loadClientArea(userId, establishmentId));
  }, [establishmentId]);

  useEffect(() => {
    if(!!resaleId) {
      return history.push('/visao-geral');
    }
  }, [resaleId]);

  useEffect(() => {
    if(isLoading) {
      hasLoadedSubscriptions.current = true;
      return;
    }

    if(!isFromLogin || !hasLoadedSubscriptions.current || hasProcessedLoginRedirect.current) {
      return;
    }

    hasProcessedLoginRedirect.current = true;

    if(subscriptions?.length == 0) {
      toast.error('Erro ao redirecionar para o cadastro de cartão');
      return;
    }

    setIsSuperlogicaUserLoading(false);

    setSuperLogicaUser({
      superlogicaId: subscriptions[0]?.superlogicaId || '',
      email: getSubscriptionEmail(subscriptions[0])
    });
  }, [isFromLogin, subscriptions, isLoading]);

  useEffect(() => {
    (async () => {
      if(isFromLogin) {
        if(!!superLogicaUser.email && !!superLogicaUser.superlogicaId) {
          const { data } = await generateToken(userId, token, superLogicaUser);
          return window.location.replace(`https://jumptecnologia.superlogica.net/clients/areadocliente/cadastro/cartao?token=${data}`);
        }
      }
    })();
  }, [isFromLogin, superLogicaUser]);

  const renderLoadAllSubscriptions = () => {
    if(!hasHiddenSubscriptions) {
      return null;
    }

    return(
      <button
        type='button'
        className={classes.loadAllButton}
        onClick={() => setIsShowingAllSubscriptions(true)}
      >
        <Typography variant='body2'>
          {(filteredSubscriptions?.length == 1) && 'Carregar todos os resultados'}
          {(filteredSubscriptions?.length > 1) && `Carregar todos os ${filteredSubscriptions?.length} resultados`}
        </Typography>
      </button>
    );
  }

  const renderLoadMoreCharges = () => {
    if(hiddenChargesSize <= 0) {
      return null;
    }

    const nextChargesSize = hiddenChargesSize > chargesPageSize ? chargesPageSize : hiddenChargesSize;

    return(
      <button
        type='button'
        className={classes.loadAllButton}
        onClick={() => setVisibleChargesSize(visibleChargesSize + chargesPageSize)}
      >
        <Typography variant='body2'>
          {(nextChargesSize == 1) && 'Carregar mais 1 resultado'}
          {(nextChargesSize > 1) && `Carregar mais ${nextChargesSize} resultados`}
        </Typography>
      </button>
    );
  }

  const getPaymentMethodName = id => {
    if(+id == 0) {
      return 'Boleto';
    }

    if(+id == 3) {
      return 'Cartão de Crédito';
    }

    return 'Transferência Bancária';
  }

  const handleFilterCharges = () => {
    setVisibleChargesSize(chargesPageSize);
    setAppliedChargeFilters({
      statusId: chargeStatusFilter,
      competence: chargeCompetenceFilter || 'current'
    });
  }

  const handleClearChargesFilters = () => {
    setVisibleChargesSize(chargesPageSize);
    setChargeStatusFilter('');
    setChargeCompetenceFilter('current');
    setAppliedChargeFilters({ statusId: '', competence: 'current' });
  }

  const renderChargesFilters = () => {
    return(
      <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '0 10px 0 10px', marginBottom: isMobile ? 10 : 0, backgroundColor: '#FFFFFF', border: '1px solid #D7D7D7' }}>
        <div style={{ width: '100%', display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: 'center', gap: 10 }}>
          <div style={{ width: isMobile ? '100%' : 200 }}>
            <Select
              name='chargeStatusFilter'
              label='Situação de pagamento'
              options={chargeStatusOptions}
              value={chargeStatusFilter}
              onChange={event => setChargeStatusFilter(event?.target?.value)}
            />
          </div>
          <div style={{ width: isMobile ? '100%' : 200 }}>
            <Select
              name='chargeCompetenceFilter'
              label='Competência'
              options={chargeCompetenceOptions}
              value={chargeCompetenceFilter}
              onChange={event => setChargeCompetenceFilter(event?.target?.value)}
            />
          </div>
        </div>
        <div style={{ width: isMobile ? '100%' : 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, marginBottom: isMobile ? 10 : 0 }}>
          <Button
            fullWidth={isMobile}
            type='button'
            color='success'
            onClick={handleFilterCharges}
          >
            Filtrar
          </Button>
          {isChargeFilterActive && (
            <IconButton type='button' onClick={handleClearChargesFilters}>
              <Close />
            </IconButton>
          )}
        </div>
      </div>
    );
  }

  const handleFilterSubscriptions = () => {
    setVisibleChargesSize(chargesPageSize);
    setAppliedSubscriptionFilters({
      superlogicaId: subscriptionBusinessFilter,
      identifierExtra: subscriptionEstablishmentFilter
    });
  }

  const handleClearSubscriptionsFilters = () => {
    setVisibleChargesSize(chargesPageSize);
    setSubscriptionBusinessFilter('');
    setSubscriptionEstablishmentFilter('');
    setAppliedSubscriptionFilters({ superlogicaId: '', identifierExtra: '' });
  }

  const renderSubscriptionsFilters = () => {
    return(
      <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '0 10px 0 10px', marginBottom: isMobile ? 10 : 0, backgroundColor: '#FFFFFF', border: '1px solid #D7D7D7' }}>
        <div style={{ width: '100%', display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: 'center', gap: 10 }}>
          <div style={{ width: isMobile ? '100%' : 200 }}>
            <Select
              name='subscriptionBusinessFilter'
              label='Negócio'
              options={subscriptionBusinessOptions}
              value={subscriptionBusinessFilter}
              onChange={event => setSubscriptionBusinessFilter(event?.target?.value)}
            />
          </div>
          <div style={{ width: isMobile ? '100%' : 200 }}>
            <Select
              name='subscriptionEstablishmentFilter'
              label='Estabelecimento'
              options={subscriptionEstablishmentOptions}
              value={subscriptionEstablishmentFilter}
              onChange={event => setSubscriptionEstablishmentFilter(event?.target?.value)}
            />
          </div>
        </div>
        <div style={{ width: isMobile ? '100%' : 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, marginBottom: isMobile ? 10 : 0 }}>
          <Button
            fullWidth={isMobile}
            type='button'
            color='success'
            onClick={handleFilterSubscriptions}
          >
            Filtrar
          </Button>
          {isSubscriptionFilterActive && (
            <IconButton type='button' onClick={handleClearSubscriptionsFilters}>
              <Close />
            </IconButton>
          )}
        </div>
      </div>
    );
  }

  const renderStatus = status => {
    if(status == 'Pago') {
      return(
        <Chip
          label='Pago'
          style={{ height: 20, color: '#43A047', backgroundColor: 'rgba(67, 160, 71, 0.24)' }}
        />
      );
    }

    if(status == 'Atrasado') {
      return(
        <Chip
          label='Atrasado'
          style={{ height: 20, color: '#F66C6B', backgroundColor: 'rgba(246, 108, 107, 0.34)' }}
        />
      );
    }

    return(
      <Chip
        label='Aberto'
        style={{ height: 20, color: 'inherit', backgroundColor: 'rgba(230, 230, 230, 0.70)' }}
      />
    );
  }

  const handleGoToSuperlogica = async (url, superlogicaId, email) => {
    if(!superlogicaId || !email) {
      return toast.error('Houve um problema ao acessar as suas assinaturas.');
    }

    const userEmail = email?.includes(';') ? email?.split(';')[0] : email;

    const { data } = await generateToken(userId, token, { superlogicaId, email: userEmail });

    if(!data) {
      return toast.error('Houve um problema ao acessar as suas assinaturas.');
    }

    return window?.open(`${url}?token=${data}`, '_blank')?.focus();
  }

  const removeDebitWarning = () => {
    return removeSuperLogicaDebitWarning(userId)
      .then(() => {
        setIsRemoveDebitWarningModalOpen(true);
        setIsPixPaymentModalOpen(false);
      })
      .catch(() => toast.error('Houve um problama ao remover o lembrete de débito.'));
  }

  const handleOpenFirstChargeModal = subscription => {
    setFirstChargeSubscription(subscription);
    setIsFirstChargeModalOpen(true);
  }

  const getSendChargeEmail = email => {
    if(!email) {
      return '';
    }

    if(email?.includes(';')) {
      if(email?.split(';')?.length > 0) {
        return email?.split(';')[0];
      }
    }

    return email;
  }

  const handleOpenSendChargeEmailModal = charge => {
    setSendChargeEmailData({
      id: charge?.id,
      email: getSendChargeEmail(charge?.email)
    });

    setSendChargeEmailError('');
    setIsSendChargeEmailModalOpen(true);
  }

  const handleCloseSendChargeEmailModal = () => {
    setIsSendChargeEmailModalOpen(false);
    setSendChargeEmailData({ id: null, email: '' });
    setSendChargeEmailError('');
  }

  const handleSendChargeEmail = () => {
    if(!sendChargeEmailData?.email) {
      setSendChargeEmailError('O e-mail é obrigatório');
      return;
    }

    if(!emailRegExp.test(sendChargeEmailData?.email)) {
      setSendChargeEmailError('O e-mail precisa ser válido');
      return;
    }

    const params = {
      id: sendChargeEmailData?.id,
      email: sendChargeEmailData?.email
    }

    return dispatch(sendClientAreaChargeEmail(userId, params)).then(() => {
      handleCloseSendChargeEmailModal();
    });
  }

  return(
    <>
      {(isFromLogin && isSuperlogicaUserLoading) && (
        <Backdrop open style={{ zIndex: 10000 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 15 }}>
            <Typography variant='h6' style={{ color: '#FFFFFF' }}>
              Redirecionando para a tela de cadastro de cartão...
            </Typography>
            <CircularProgress />
          </div>
        </Backdrop>
      )}
      {isFirstChargeModalOpen && (
        <ListClientsFirstCharge
          open={isFirstChargeModalOpen}
          onClose={() => setIsFirstChargeModalOpen(false)}
          subscription={firstChargeSubscription}
          hasCreditCard={firstChargeSubscription?.hasCreditCard}
        />
      )}
      {isSendChargeEmailModalOpen && (
        <Modal
          scroll="body"
          title="Enviar comprovante por e-mail"
          open={isSendChargeEmailModalOpen}
          onClose={handleCloseSendChargeEmailModal}
          maxWidth="sm"
        >
          <Input
            type="email"
            name="email"
            label="E-mail"
            value={sendChargeEmailData?.email}
            error={!!sendChargeEmailError}
            helperText={sendChargeEmailError}
            onChange={event => {
              setSendChargeEmailError('');

              setSendChargeEmailData({
                ...sendChargeEmailData,
                email: event.target.value
              });
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button
              type="button"
              variant="outlined"
              color="tertiary"
              loading={isSendEmailLoading}
              onClick={handleCloseSendChargeEmailModal}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              color="primary"
              loading={isSendEmailLoading}
              onClick={handleSendChargeEmail}
            >
              Enviar
            </Button>
          </div>
        </Modal>
      )}
      {isRemoveDebitWarningModalOpen && (
        <Modal
          scroll="body"
          title="Lembrete de débito"
          open={isRemoveDebitWarningModalOpen}
          onClose={() => setIsRemoveDebitWarningModalOpen(false)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <Typography variant='h5' color='primary'>Lembrete de débito retirado!</Typography>
            <Typography variant='h6' color='primary' style={{ margin: '20px 0 20px 0' }}>Se não identificarmos o pagamento em até 3 dias úteis, o sistema será desativado.</Typography>
            <Typography variant='h6' color='primary' style={{ margin: '0 0 20px 0' }}>Não se preocupe em enviar o comprovante, o sistema acusa automaticamente o seu pagamento.</Typography>
            <Typography variant='h5' color='primary'>Obrigado pela parceria!</Typography>
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: 20 }}>
              <Button
                color='primary'
                onClick={() => setIsRemoveDebitWarningModalOpen(false)}
              >
                Ok, obrigado
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {isPixPaymentModalOpen && (
        <Modal
          scroll="body"
          title="Pagamento"
          open={isPixPaymentModalOpen}
          onClose={() => setIsPixPaymentModalOpen(false)}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '100%', backgroundColor: '#FCFCFC', border: '1px solid #D7D7D7', borderRadius: 5, padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography color='primary'>
                  <b>Pague a fatura referente ao CPF/CNPJ</b>
                </Typography>
                <Typography color='textSecondary'>{maskCNPJ(pixPaymentModalData?.documentId)}</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography color='primary'>
                  <b>Valor</b>
                </Typography>
                <Typography color='textSecondary'>{convertToReal(pixPaymentModalData?.amount)}</Typography>
              </div>
            </div>
            <Typography variant='h6' color='primary' style={{ textAlign: 'center', marginTop: 10 }}>
              Leia o QR Code ou copie e cole o código PIX
            </Typography>
            {(!!pixPaymentModalData?.pixCode && !!pixPaymentModalData?.pixQrCode) && (
              <div>
                <img
                  width='300px'
                  height='300px'
                  src={pixPaymentModalData?.pixQrCode}
                />
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%', marginTop: 10 }}>
              <Typography color='textSecondary' style={{ textAlign: 'center', wordBreak: 'break-all' }}>
                {pixPaymentModalData?.pixCode}
              </Typography>
              <Button
                type='button'
                color='primary'
                onClick={() => {
                  navigator.clipboard.writeText(pixPaymentModalData?.pixCode);
                  return toast.success('Código PIX copiado!');
                }}
                startIcon={
                  <FontAwesomeIcon
                    icon={faCopy}
                    color="#FFFFFF"
                    size="lg"
                  />
                }
              >
                Copiar Código
              </Button>
            </div>
            <Typography variant='h6' style={{ textAlign: 'center', marginTop: 10 }}>
              <b>O pagamento demora até 2 dias úteis para ser identificado.</b>
            </Typography>
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 10, marginTop: 10 }}>
              <Button
                type='button'
                variant='outlined'
                color='tertiary'
                onClick={() => setIsPixPaymentModalOpen(false)}
              >
                Cancelar
              </Button>
              {!isMobile && (
                <>
                  {(isDebitWarning || isUserAdmin) && (
                    <Button
                      type='button'
                      color='primary'
                      onClick={removeDebitWarning}
                    >
                      Já Paguei
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </Modal>
      )}
      <Grid container>
        <Grid item xs={12}>
          <Loader isLoading={isLoading}>
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10, padding: 10 }}>
                <AccountBalance fontSize='large' style={{ color: '#43a047' }} />
                <Typography color='primary' variant='h5'>
                  Assinaturas
                </Typography>
              </div>
              {renderSubscriptionsFilters()}
              {isMobile && (
                <TableMobile
                  loading={isLoading}
                  data={visibleSubscriptions?.map(item => {
                    return {
                      ...item,
                      business: {
                        index: 1,
                        label: 'Negócio',
                        value: (
                          <>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {`CNPJ ${maskCNPJ(item?.documentId) || '--'}`}
                            </Typography>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {item?.fantasyName || '--'}
                            </Typography>
                          </>
                        )
                      },
                      planDetails: {
                        index: 2,
                        label: 'Detalhes do plano',
                        value: (
                          <>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {item?.identifier || ''}
                            </Typography>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {item?.plan?.title || '--'}
                            </Typography>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {item?.plan?.description || ''}
                            </Typography>
                          </>
                        )
                      },
                      charge: {
                        index: 3,
                        label: 'Cobrança',
                        value: (
                          <>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {formatCharge(item?.charge)}
                            </Typography>
                            {!!item?.renewalDate && (
                              <div style={{ width: 'fit-content', padding: 5, backgroundColor: 'rgba(67, 160, 71, 0.7)', borderRadius: 8 }}>
                                <Typography variant='body2' style={{ color: '#FFFFFF' }}>
                                  Renovará em {moment(item?.renewalDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                                </Typography>
                              </div>
                            )}
                          </>
                        )
                      }
                    }
                  })}
                  actions={[
                    {
                      action: rowData => (
                        <Tooltip placement='top' title='Editar meio de pagamento' enterTouchDelay={0}>
                          <IconButton onClick={() => {
                            return handleGoToSuperlogica('https://jumptecnologia.superlogica.net/clients/areadocliente/clientes/formadepagamento', rowData?.superlogicaId, rowData?.email);
                          }}>
                            <FontAwesomeIcon
                              icon={faMoneyBill}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    },
                    {
                      action: rowData => (
                        <Tooltip placement='top' title='Editar cadastro do cliente' enterTouchDelay={0}>
                          <IconButton onClick={() => {
                            return handleGoToSuperlogica('https://jumptecnologia.superlogica.net/clients/areadocliente/cadastro/index', rowData?.superlogicaId, rowData?.email);
                          }}>
                            <FontAwesomeIcon
                              icon={faPen}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    },
                    {
                      action: rowData => +rowData?.firstPayment == 1 && (
                        <Tooltip placement='top' title='Gerar primeiro pagamento' enterTouchDelay={0}>
                          <IconButton onClick={() => handleOpenFirstChargeModal(rowData)}>
                            <FontAwesomeIcon
                              icon={faFileInvoiceDollar}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  ]}
                />
              )}
              {isMobile && (
                <div style={{ padding: '0 10px 0 10px' }}>
                  {renderLoadAllSubscriptions()}
                </div>
              )}
              {!isMobile && (
                <div className={classes.clientAreaTable}>
                  <MTable
                    loading={isLoading}
                    headers={subscriptionHeaders}
                    data={visibleSubscriptions?.map(item => {
                      return {
                        ...item,
                        business: (
                          <>
                            <Typography variant='body2'>
                              {`CNPJ ${maskCNPJ(item?.documentId) || '--'}`}
                            </Typography>
                            <Typography variant='body2'>
                              {item?.fantasyName || '--'}
                            </Typography>
                          </>
                        ),
                        planDetails: (
                          <>
                            <Typography variant='body2'>
                              {item?.identifier || ''}
                            </Typography>
                            <Typography variant='body2'>
                              {item?.plan?.title || '--'}
                            </Typography>
                            <Typography variant='body2'>
                              {item?.plan?.description ? `• ${item?.plan?.description}` : ''}
                            </Typography>
                          </>
                        ),
                        charge: (
                          <>
                            <Typography variant='body2'>
                              {formatCharge(item?.charge)}
                            </Typography>
                            {!!item?.renewalDate && (
                              <div style={{ width: 'fit-content', margin: '0 auto', padding: 5, backgroundColor: 'rgba(67, 160, 71, 0.7)', borderRadius: 8 }}>
                                <Typography variant='body2' style={{ color: '#FFFFFF' }}>
                                  Renovará em {moment(item?.renewalDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                                </Typography>
                              </div>
                            )}
                          </>
                        )
                      }
                    })}
                    actions={[
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faMoneyBill}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Editar meio de pagamento',
                        onClick: (_, rowData) => {
                          return handleGoToSuperlogica('https://jumptecnologia.superlogica.net/clients/areadocliente/clientes/formadepagamento', rowData?.superlogicaId, rowData?.email);
                        }
                      }),
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faPen}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Editar cadastro do cliente',
                        onClick: (_, rowData) => {
                          return handleGoToSuperlogica('https://jumptecnologia.superlogica.net/clients/areadocliente/cadastro/index', rowData?.superlogicaId, rowData?.email);
                        }
                      }),
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faFileInvoiceDollar}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Gerar primeiro pagamento',
                        onClick: (_, rowData) => handleOpenFirstChargeModal(rowData),
                        hidden: +rowData?.firstPayment != 1
                      })
                    ]}
                  />
                  {renderLoadAllSubscriptions()}
                </div>
              )}
            </>
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10, padding: 10, marginTop: 10 }}>
                <Receipt fontSize='large' style={{ color: '#43a047' }} />
                <Typography color='primary' variant='h5'>
                  Cobranças
                </Typography>
              </div>
              {renderChargesFilters()}
              {isMobile && (
                <TableMobile
                  loading={isLoading}
                  data={visibleCharges?.map(item => {
                    const daysLate = getDaysLate(item?.liquidationDate, item?.dueDate);

                    return {
                      ...item,
                      rawAmount: item?.amount,
                      situation: {
                        index: 1,
                        label: 'Situação',
                        value: renderStatus(item?.status)
                      },
                      dueDate: {
                        index: 2,
                        label: 'Vencimento',
                        value: (
                          <>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {formatDate(item?.dueDate)}
                            </Typography>
                            {!!item?.liquidationDate && (
                              <Typography color='textSecondary' variant='subtitle2'>
                                {`Liquidado em ${formatDate(item?.liquidationDate)}`}
                              </Typography>
                            )}
                            {daysLate > 0 && (
                              <Typography color='textSecondary' variant='subtitle2'>
                                {daysLate == 1 ? '1 dia de atraso' : `${daysLate} dias de atraso`}
                              </Typography>
                            )}
                          </>
                        )
                      },
                      business: {
                        index: 3,
                        label: 'Negócio',
                        value: (
                          <>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {item?.fantasyName || '--'}
                            </Typography>
                            <Typography color='textSecondary' variant='subtitle2'>
                              {`CNPJ ${maskCNPJ(item?.documentId) || '--'}`}
                            </Typography>
                            {renderChargeNf(item?.nf, { color: 'textSecondary', variant: 'subtitle2' })}
                            {!!item?.creditCardError && (
                              <Typography color='error' variant='body2'>
                                Erro: {item?.creditCardError}
                              </Typography>
                            )}
                          </>
                        )
                      },
                      paymentMethod: {
                        index: 4,
                        label: 'Forma de pagamento',
                        value: (
                          <Typography color='textSecondary' variant='subtitle2'>
                            {getPaymentMethodName(item?.paymentMethodId)}
                          </Typography>
                        )
                      },
                      amount: {
                        index: 5,
                        label: 'Valor',
                        value: (
                          <Typography color='textSecondary' variant='subtitle2'>
                            {convertToReal(item?.amount)}
                          </Typography>
                        )
                      }
                    }
                  })}
                  actions={[
                    {
                      action: rowData => (+rowData?.paymentMethodId == 0 || +rowData?.paymentMethodId == 3) && !!rowData?.creditCardError && (
                        <Tooltip placement='top' title='Reprocessar pagamento' enterTouchDelay={0}>
                          <IconButton onClick={() => dispatch(updateReprocessCreditCardCharge(userId, establishmentId, { id: rowData?.id }))}>
                            <FontAwesomeIcon
                              icon={faRotateRight}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    },
                    {
                      action: rowData => +rowData?.paymentMethodId != 3 && +rowData?.statusId == 0 && !!rowData?.paymentLink && (
                        <Tooltip placement='top' title='Boleto a pagar' enterTouchDelay={0}>
                          <IconButton onClick={() => window.open(rowData?.paymentLink, '_blank')}>
                            <FontAwesomeIcon
                              icon={faBarcode}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    },
                    {
                      action: rowData => +rowData?.paymentMethodId != 3 && +rowData?.statusId == 0 && !!rowData?.pixQrCode && (
                        <Tooltip placement='top' title='Código PIX' enterTouchDelay={0}>
                          <IconButton onClick={async () => {
                            const qrCode = await QRCode.toDataURL(rowData?.pixQrCode);

                            setPixPaymentModalData({
                              documentId: rowData?.documentId,
                              amount: rowData?.rawAmount,
                              pixCode: rowData?.pixQrCode,
                              pixQrCode: qrCode
                            });

                            setIsPixPaymentModalOpen(true);
                          }}>
                            <FontAwesomeIcon
                              icon={faQrcode}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    },
                    {
                      action: rowData => +rowData?.paymentMethodId != 3 && +rowData?.statusId == 1 && !!rowData?.receiptLink && (
                        <Tooltip placement='top' title='Download do recibo' enterTouchDelay={0}>
                          <IconButton onClick={() => window.open(rowData?.receiptLink, '_blank')}>
                            <FontAwesomeIcon
                              icon={faReceipt}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    },
                    {
                      action: rowData => +rowData?.paymentMethodId != 3 && +rowData?.statusId == 1 && (
                        <Tooltip placement='top' title='Enviar comprovante por email' enterTouchDelay={0}>
                          <IconButton onClick={() => handleOpenSendChargeEmailModal(rowData)}>
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              color="#022A5C"
                              size="xs"
                            />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  ]}
                />
              )}
              {isMobile && (
                <div style={{ padding: '0 10px 0 10px' }}>
                  {renderLoadMoreCharges()}
                </div>
              )}
              {!isMobile && (
                <div className={classes.clientAreaTable}>
                  <MTable
                    loading={isLoading}
                    headers={chargeHeaders}
                    data={visibleCharges?.map(item => {
                      const daysLate = getDaysLate(item?.liquidationDate, item?.dueDate);

                      return {
                        ...item,
                        rawAmount: item?.amount,
                        situation: renderStatus(item?.status),
                        dueDate: (
                          <>
                            <Typography variant='body2'>
                              {formatDate(item?.dueDate)}
                            </Typography>
                            {!!item?.liquidationDate && (
                              <Typography variant='body2'>
                                {`Liquidado em ${formatDate(item?.liquidationDate)}`}
                              </Typography>
                            )}
                            {daysLate > 0 && (
                              <Typography variant='body2'>
                                {daysLate == 1 ? '1 dia de atraso' : `${daysLate} dias de atraso`}
                              </Typography>
                            )}
                          </>
                        ),
                        business: (
                          <>
                            <Typography variant='body2'>
                              {item?.fantasyName || '--'}
                            </Typography>
                            <Typography variant='body2'>
                              {`CNPJ ${maskCNPJ(item?.documentId) || '--'}`}
                            </Typography>
                            {renderChargeNf(item?.nf, { variant: 'body2' })}
                            {!!item?.creditCardError && (
                              <Typography color='error' variant='body2'>
                                Erro: {item?.creditCardError}
                              </Typography>
                            )}
                          </>
                        ),
                        paymentMethod: (
                          <Typography variant='body2'>
                            {getPaymentMethodName(item?.paymentMethodId)}
                          </Typography>
                        ),
                        amount: (
                          <Typography variant='body2'>
                            {convertToReal(item?.amount)}
                          </Typography>
                        )
                      }
                    })}
                    actions={[
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faRotateRight}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Reprocessar pagamento',
                        onClick: (_, rowData) => dispatch(updateReprocessCreditCardCharge(userId, establishmentId, { id: rowData?.id })),
                        hidden: (+rowData?.paymentMethodId != 0 && +rowData?.paymentMethodId != 3) || !rowData?.creditCardError
                      }),
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faBarcode}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Boleto a pagar',
                        onClick: () => window.open(rowData?.paymentLink, '_blank'),
                        hidden: +rowData?.paymentMethodId == 3 || +rowData?.statusId == 1 || !rowData?.paymentLink
                      }),
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faQrcode}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Código PIX',
                        onClick: async () => {
                          const qrCode = await QRCode.toDataURL(rowData?.pixQrCode);

                          setPixPaymentModalData({
                            documentId: rowData?.documentId,
                            amount: rowData?.rawAmount,
                            pixCode: rowData?.pixQrCode,
                            pixQrCode: qrCode
                          });

                          setIsPixPaymentModalOpen(true);
                        },
                        hidden: +rowData?.paymentMethodId == 3 || +rowData?.statusId == 1 || !rowData?.pixQrCode
                      }),
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faReceipt}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Download do recibo',
                        onClick: () => window.open(rowData?.receiptLink, '_blank'),
                        hidden: +rowData?.paymentMethodId == 3 || +rowData?.statusId != 1 || !rowData?.receiptLink
                      }),
                      rowData => ({
                        icon: () => (
                          <FontAwesomeIcon
                            icon={faEnvelope}
                            color="#022A5C"
                            size="xs"
                          />
                        ),
                        tooltip: 'Enviar comprovante por email',
                        onClick: (_, rowData) => handleOpenSendChargeEmailModal(rowData),
                        hidden: +rowData?.paymentMethodId == 3 || +rowData?.statusId != 1
                      })
                    ]}
                  />
                  {renderLoadMoreCharges()}
                </div>
              )}
            </>
          </Loader>
        </Grid>
      </Grid>
    </>
  );
}